import React, { useState } from 'react'
import {
  SYNTHETIC_BODY_DISCOMFORTS,
  SYNTHETIC_DISCOMFORT_METRICS,
} from './syntheticDashboardData'
import { Activity, ShieldAlert, RotateCcw } from 'lucide-react'

interface BodyDiscomfortPanelProps {
  onSelectAthlete?: (athleteId: string) => void
  isCoachView?: boolean
}

export const BodyDiscomfortPanel: React.FC<BodyDiscomfortPanelProps> = ({
  onSelectAthlete,
  isCoachView = false,
}) => {
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null)
  const [bodyOrientation, setBodyOrientation] = useState<'both' | 'front' | 'back'>('both')

  // En vista coach, los datos sanitarios privados no se muestran
  if (isCoachView) {
    return (
      <div style={{
        background: 'var(--surface-card, #ffffff)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '10px',
        padding: '1.25rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.5rem' }}>
          <ShieldAlert size={18} color="var(--brand-red, #b91c1c)" />
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
            Disponibilidad y Restricciones Operativas para el Entrenamiento
          </h3>
        </div>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary, #475569)', margin: '0 0 1rem 0' }}>
          Información operativa autorizada para el cuerpo técnico. Los informes clínicos y detalles anatómicos privados están reservados al servicio médico.
        </p>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '10px',
        }}>
          <div style={{
            background: 'var(--surface-subtle, #f8fafc)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '12px',
          }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Atletas con Restricción Activa
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#b45309', margin: '4px 0', fontVariantNumeric: 'tabular-nums' }}>
              2 jugadores
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #475569)' }}>
              #88 Mateo Benítez (Sin contacto / sprints máx.)<br />
              #5 David Romero (Carga controlada en saltos)
            </div>
          </div>

          <div style={{
            background: 'var(--surface-subtle, #f8fafc)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '12px',
          }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Bajas Médicas en Rehabilitación
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--brand-red, #b91c1c)', margin: '4px 0', fontVariantNumeric: 'tabular-nums' }}>
              1 jugador
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #475569)' }}>
              #33 Diego Santos (Rehabilitación activa)
            </div>
          </div>

          <div style={{
            background: 'var(--surface-subtle, #f8fafc)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '8px',
            padding: '12px',
          }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', textTransform: 'uppercase' }}>
              Revisión Previas a la Concentración
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1d4ed8', margin: '4px 0', fontVariantNumeric: 'tabular-nums' }}>
              1 valoración programada
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #475569)' }}>
              #17 Carlos Vega (14 Mayo 2026)
            </div>
          </div>
        </div>
      </div>
    )
  }

  const selectedZone = selectedZoneId
    ? SYNTHETIC_BODY_DISCOMFORTS.find((z) => z.id === selectedZoneId)
    : null

  const activeZonesWithEpisodes = SYNTHETIC_BODY_DISCOMFORTS.filter((z) => z.episodesCount > 0)

  return (
    <div style={{
      background: 'var(--surface-card, #ffffff)',
      border: '1px solid var(--surface-border, #e2e8f0)',
      borderRadius: '10px',
      padding: '1.25rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.03)',
    }}>
      {/* Header and Context */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '0.75rem',
        marginBottom: '1rem',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Activity size={18} color="var(--brand-red, #b91c1c)" />
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: 'var(--text-main, #0f172a)' }}>
              Mapa Anatómico de Molestias e Incidencias Activas
            </h3>
          </div>
          <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: 'var(--text-secondary, #475569)' }}>
            Distribución por zona corporal según formularios semanales y valoraciones fisioterápicas del ciclo
          </p>
        </div>

        {/* View toggle (Front / Back / Both) */}
        <div style={{
          display: 'inline-flex',
          background: 'var(--surface-subtle, #f8fafc)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '6px',
          padding: '2px',
          fontSize: '0.75rem',
        }}>
          <button
            type="button"
            onClick={() => setBodyOrientation('both')}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              border: 'none',
              background: bodyOrientation === 'both' ? '#ffffff' : 'transparent',
              color: bodyOrientation === 'both' ? 'var(--text-main, #0f172a)' : 'var(--text-secondary, #475569)',
              fontWeight: bodyOrientation === 'both' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Frente y Espalda
          </button>
          <button
            type="button"
            onClick={() => setBodyOrientation('front')}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              border: 'none',
              background: bodyOrientation === 'front' ? '#ffffff' : 'transparent',
              color: bodyOrientation === 'front' ? 'var(--text-main, #0f172a)' : 'var(--text-secondary, #475569)',
              fontWeight: bodyOrientation === 'front' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Anterior (Frente)
          </button>
          <button
            type="button"
            onClick={() => setBodyOrientation('back')}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              border: 'none',
              background: bodyOrientation === 'back' ? '#ffffff' : 'transparent',
              color: bodyOrientation === 'back' ? 'var(--text-main, #0f172a)' : 'var(--text-secondary, #475569)',
              fontWeight: bodyOrientation === 'back' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Posterior (Espalda)
          </button>
        </div>
      </div>

      {/* Critical Distinction Banner */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '0.75rem',
        background: 'var(--surface-subtle, #f8fafc)',
        border: '1px solid var(--surface-border, #e2e8f0)',
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        marginBottom: '1.25rem',
        fontSize: '0.82rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div>
            <span style={{ color: 'var(--text-muted, #64748b)' }}>Jugadores afectados: </span>
            <strong style={{ color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
              {SYNTHETIC_DISCOMFORT_METRICS.totalAffectedPlayers} atletas
            </strong>
          </div>
          <span style={{ color: 'var(--surface-border, #cbd5e1)' }}>|</span>
          <div>
            <span style={{ color: 'var(--text-muted, #64748b)' }}>Episodios activos: </span>
            <strong style={{ color: 'var(--text-main, #0f172a)', fontVariantNumeric: 'tabular-nums' }}>
              {SYNTHETIC_DISCOMFORT_METRICS.totalEpisodes} registros
            </strong>
          </div>
          <span style={{ color: 'var(--surface-border, #cbd5e1)' }}>|</span>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary, #475569)' }}>
            Nota clínica: se diferencia <em>molestia autodeclarada</em> de <em>lesión diagnosticada</em>.
          </div>
        </div>

        {selectedZoneId && (
          <button
            type="button"
            onClick={() => setSelectedZoneId(null)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '3px 8px',
              borderRadius: '4px',
              border: '1px solid var(--surface-border, #cbd5e1)',
              background: '#ffffff',
              fontSize: '0.72rem',
              color: 'var(--text-secondary, #475569)',
              cursor: 'pointer',
            }}
          >
            <RotateCcw size={12} />
            <span>Restablecer filtro</span>
          </button>
        )}
      </div>

      {/* Main Grid: Silhouette Visual Left + Bars & Details Right */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1.5rem',
        alignItems: 'start',
      }}>
        {/* Silhouette Visual Display */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: 'var(--surface-subtle, #f8fafc)',
          border: '1px solid var(--surface-border, #e2e8f0)',
          borderRadius: '8px',
          padding: '1rem',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1.5rem',
            width: '100%',
          }}>
            {/* Front View */}
            {(bodyOrientation === 'both' || bodyOrientation === 'front') && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', marginBottom: '6px' }}>
                  ANTERIOR (FRENTE)
                </div>
                <svg
                  viewBox="0 0 100 220"
                  width="110"
                  height="220"
                  style={{ display: 'block', margin: '0 auto' }}
                >
                  {/* Cabeza */}
                  <circle cx="50" cy="20" r="12" fill="#cbd5e1" />
                  {/* Cuello */}
                  <rect x="46" y="32" width="8" height="8" rx="2" fill="#94a3b8" />
                  {/* Torso / Pecho */}
                  <path
                    d="M32 40 L68 40 L62 90 L38 90 Z"
                    fill="#e2e8f0"
                    stroke="#94a3b8"
                    strokeWidth="1.5"
                  />
                  {/* Brazos */}
                  <rect x="22" y="42" width="8" height="50" rx="4" fill="#cbd5e1" />
                  <rect x="70" y="42" width="8" height="50" rx="4" fill="#cbd5e1" />
                  {/* Caderas */}
                  <path d="M38 90 L62 90 L58 110 L42 110 Z" fill="#cbd5e1" />
                  {/* Cuádriceps */}
                  <rect x="36" y="112" width="11" height="38" rx="3" fill="#cbd5e1" />
                  <rect x="53" y="112" width="11" height="38" rx="3" fill="#cbd5e1" />
                  {/* Rodilla Derecha (1 episodio David Romero) */}
                  <circle
                    cx="58.5"
                    cy="155"
                    r="6.5"
                    fill="#f59e0b"
                    stroke="#b45309"
                    strokeWidth="1.5"
                    style={{ cursor: 'pointer', transition: 'transform 0.15s ease' }}
                    onClick={() => setSelectedZoneId('rodilla')}
                  />
                  {/* Rodilla Izquierda (sin molestias) */}
                  <circle
                    cx="41.5"
                    cy="155"
                    r="5.5"
                    fill="#cbd5e1"
                  />
                  {/* Gemelos / Tibias */}
                  <rect x="37" y="163" width="9" height="36" rx="2" fill="#cbd5e1" />
                  <rect x="54" y="163" width="9" height="36" rx="2" fill="#cbd5e1" />
                  {/* Tobillo Derecho (1 episodio Diego Santos) */}
                  <circle
                    cx="58.5"
                    cy="204"
                    r="6.5"
                    fill="#b91c1c"
                    stroke="#991b1b"
                    strokeWidth="1.5"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedZoneId('tobillo')}
                  />
                  <circle cx="41.5" cy="204" r="5" fill="#cbd5e1" />
                </svg>
              </div>
            )}

            {/* Back View */}
            {(bodyOrientation === 'both' || bodyOrientation === 'back') && (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted, #64748b)', marginBottom: '6px' }}>
                  POSTERIOR (ESPALDA)
                </div>
                <svg
                  viewBox="0 0 100 220"
                  width="110"
                  height="220"
                  style={{ display: 'block', margin: '0 auto' }}
                >
                  {/* Cabeza */}
                  <circle cx="50" cy="20" r="12" fill="#94a3b8" />
                  {/* Espalda alta */}
                  <path
                    d="M32 40 L68 40 L64 70 L36 70 Z"
                    fill="#e2e8f0"
                    stroke="#94a3b8"
                    strokeWidth="1.5"
                  />
                  {/* Brazos */}
                  <rect x="22" y="42" width="8" height="50" rx="4" fill="#cbd5e1" />
                  <rect x="70" y="42" width="8" height="50" rx="4" fill="#cbd5e1" />
                  {/* Lumbar (1 episodio Carlos Vega) */}
                  <rect
                    x="37"
                    y="72"
                    width="26"
                    height="18"
                    rx="3"
                    fill="#f59e0b"
                    stroke="#b45309"
                    strokeWidth="1.5"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedZoneId('lumbar')}
                  />
                  {/* Glúteos */}
                  <path d="M38 92 L62 92 L58 112 L42 112 Z" fill="#cbd5e1" />
                  {/* Isquiotibiales (2 episodios Mateo Benítez e Iker) */}
                  <rect
                    x="53"
                    y="114"
                    width="12"
                    height="36"
                    rx="3"
                    fill="#b91c1c"
                    stroke="#991b1b"
                    strokeWidth="1.5"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedZoneId('isquiotibiales')}
                  />
                  <rect
                    x="35"
                    y="114"
                    width="12"
                    height="36"
                    rx="3"
                    fill="#f59e0b"
                    stroke="#b45309"
                    strokeWidth="1.5"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedZoneId('isquiotibiales')}
                  />
                  {/* Hueco poplíteo */}
                  <circle cx="41" cy="154" r="5" fill="#cbd5e1" />
                  <circle cx="59" cy="154" r="5" fill="#cbd5e1" />
                  {/* Gemelos / Aquiles */}
                  <rect x="37" y="162" width="9" height="38" rx="2" fill="#cbd5e1" />
                  <rect x="54" y="162" width="9" height="38" rx="2" fill="#cbd5e1" />
                  {/* Talones / Tobillos */}
                  <circle cx="41.5" cy="204" r="5" fill="#cbd5e1" />
                  <circle
                    cx="58.5"
                    cy="204"
                    r="6.5"
                    fill="#b91c1c"
                    stroke="#991b1b"
                    strokeWidth="1.5"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedZoneId('tobillo')}
                  />
                </svg>
              </div>
            )}
          </div>

          <div style={{
            fontSize: '0.72rem',
            color: 'var(--text-muted, #64748b)',
            marginTop: '0.75rem',
            textAlign: 'center',
          }}>
            Haz clic en los puntos anatómicos coloreados para filtrar los detalles.
          </div>
        </div>

        {/* Bar Distribution & Details */}
        <div>
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            color: 'var(--text-muted, #64748b)',
            marginBottom: '0.5rem',
          }}>
            Frecuencia por Región Corporal
          </div>

          {/* Bar Chart Representation */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '1rem' }}>
            {SYNTHETIC_BODY_DISCOMFORTS.map((zone) => {
              const isSelected = selectedZoneId === zone.id
              const maxEpisodes = 3
              const widthPct = (zone.episodesCount / maxEpisodes) * 100

              return (
                <div
                  key={zone.id}
                  onClick={() => setSelectedZoneId(isSelected ? null : zone.id)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: `1px solid ${isSelected ? 'var(--brand-red, #b91c1c)' : 'var(--surface-border, #e2e8f0)'}`,
                    background: isSelected ? 'rgba(185, 28, 28, 0.04)' : '#ffffff',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: '3px' }}>
                    <span style={{ fontWeight: isSelected ? 800 : 600, color: 'var(--text-main, #0f172a)' }}>
                      {zone.label}
                    </span>
                    <span style={{ color: 'var(--text-muted, #64748b)', fontVariantNumeric: 'tabular-nums' }}>
                      {zone.episodesCount} {zone.episodesCount === 1 ? 'episodio' : 'episodios'} · {zone.affectedAthletesCount} {zone.affectedAthletesCount === 1 ? 'atleta' : 'atletas'}
                    </span>
                  </div>

                  <div style={{
                    width: '100%',
                    height: '6px',
                    background: 'var(--surface-subtle, #f1f5f9)',
                    borderRadius: '999px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.max(widthPct, 0)}%`,
                      height: '100%',
                      background: zone.episodesCount >= 2 ? 'var(--brand-red, #b91c1c)' : zone.episodesCount === 1 ? '#d97706' : '#cbd5e1',
                      borderRadius: '999px',
                    }} />
                  </div>
                </div>
              )
            })}
          </div>

          {/* Accessible Table Equivalent */}
          <div style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            color: 'var(--text-muted, #64748b)',
            marginBottom: '0.5rem',
          }}>
            {selectedZone ? `Registros en: ${selectedZone.label}` : 'Listado Accesible de Registros Activos'}
          </div>

          <div style={{
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '6px',
            overflow: 'hidden',
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.78rem',
              textAlign: 'left',
            }}>
              <thead style={{ background: 'var(--surface-subtle, #f8fafc)', borderBottom: '1px solid var(--surface-border, #e2e8f0)' }}>
                <tr>
                  <th style={{ padding: '6px 10px', color: 'var(--text-muted, #64748b)', fontWeight: 700 }}>Atleta</th>
                  <th style={{ padding: '6px 10px', color: 'var(--text-muted, #64748b)', fontWeight: 700 }}>Zona y Lateralidad</th>
                  <th style={{ padding: '6px 10px', color: 'var(--text-muted, #64748b)', fontWeight: 700 }}>Naturaleza</th>
                  <th style={{ padding: '6px 10px', color: 'var(--text-muted, #64748b)', fontWeight: 700 }}>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {(selectedZone ? selectedZone.details : activeZonesWithEpisodes.flatMap((z) => z.details)).map((ep, idx) => (
                  <tr
                    key={`${ep.athleteId}-${idx}`}
                    style={{
                      borderBottom: '1px solid var(--surface-border, #f1f5f9)',
                      cursor: onSelectAthlete ? 'pointer' : 'default',
                    }}
                    onClick={() => onSelectAthlete && onSelectAthlete(ep.athleteId)}
                  >
                    <td style={{ padding: '6px 10px', fontWeight: 700, color: 'var(--text-main, #0f172a)' }}>
                      #{ep.athleteDorsal} {ep.athleteName}
                    </td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-secondary, #475569)' }}>
                      {ep.laterality !== 'central' ? `${ep.laterality.charAt(0).toUpperCase() + ep.laterality.slice(1)}` : 'Central'}
                    </td>
                    <td style={{ padding: '6px 10px' }}>
                      <span style={{
                        padding: '1px 6px',
                        borderRadius: '4px',
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        background: ep.type === 'baja_medica' ? '#fee2e2' : ep.type === 'valoracion_fisioterapia' ? '#fef3c7' : '#e0f2fe',
                        color: ep.type === 'baja_medica' ? '#b91c1c' : ep.type === 'valoracion_fisioterapia' ? '#b45309' : '#0284c7',
                      }}>
                        {ep.type === 'baja_medica' ? 'Baja Médica' : ep.type === 'valoracion_fisioterapia' ? 'Fisioterapia' : 'Autodeclarada'}
                      </span>
                    </td>
                    <td style={{ padding: '6px 10px', color: 'var(--text-muted, #64748b)', fontVariantNumeric: 'tabular-nums' }}>
                      {ep.reportedDate}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
